import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconFile,
  IconFileSpreadsheet,
  IconFilterOff,
  IconHeadset,
  IconMessageCircle,
  IconPrinter,
  IconRefresh,
  IconSearch,
  IconSettings,
  IconTicket,
  IconUser,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const STATUS_OPTIONS = ['Açık', 'İşlemde', 'Çözüldü', 'Kapatıldı'];
const PRIORITY_OPTIONS = ['Düşük', 'Orta', 'Yüksek', 'Kritik'];

const priorityColor = (priority) => ({
  Düşük: 'gray',
  Orta: 'blue',
  Yüksek: 'orange',
  Kritik: 'red',
}[priority] || 'blue');

const statusColor = (status) => ({
  Açık: 'red',
  İşlemde: 'yellow',
  Çözüldü: 'teal',
  Kapatıldı: 'gray',
}[status] || 'blue');

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const employeeName = (ticket) => {
  if (!ticket?.employee) return '-';
  return `${ticket.employee.first_name || ''} ${ticket.employee.last_name || ''}`.trim() || '-';
};

function StatCard({ title, value, subtitle, icon, color, background }) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="lg"
      style={{ borderColor: '#e9ecef', boxShadow: '0 4px 16px rgba(0,0,0,.035)' }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={3}>
          <Text c="dimmed" size="sm" fw={500}>{title}</Text>
          <Text fw={800} size="28px" lh={1.15}>{value}</Text>
          <Text c="dimmed" size="xs">{subtitle}</Text>
        </Stack>
        <ThemeIcon size={44} radius="xl" color={color} variant="light" style={{ background }}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

export default function ITSupportAdminPage() {
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [overview, setOverview] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const [commentText, setCommentText] = useState('');
  const [statusForm, setStatusForm] = useState({ status: '', resolution_note: '' });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState(false);

  const fetchOverview = async () => {
    try {
      const res = await api.get('/it-support/overview-all');
      setOverview(res.data);
    } catch (err) {
      console.error('Özet veriler alınamadı', err);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const res = await api.get('/it-support/tickets', { params });
      setTickets(res.data || []);
    } catch (err) {
      notifications.show({
        title: 'Destek kayıtları alınamadı',
        message: err.response?.data?.detail || 'Lütfen tekrar deneyin.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchOverview(), fetchTickets()]);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchTickets();
  }, [debouncedSearch, statusFilter, priorityFilter]);

  useEffect(() => {
    fetchOverview();
  }, []);

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter('');
    setPriorityFilter('');
  };

  const openTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/it-support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
      setStatusForm({
        status: res.data.status || '',
        resolution_note: res.data.resolution_note || '',
      });
      setCommentText('');
      setDrawerOpened(true);
    } catch (err) {
      notifications.show({
        title: 'Talep açılamadı',
        message: err.response?.data?.detail || 'Talep detayı alınamadı.',
        color: 'red',
      });
    }
  };

  const handleAddComment = async () => {
    if (!selectedTicket || !commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await api.post(`/it-support/tickets/${selectedTicket.id}/admin-comments`, {
        comment: commentText.trim(),
      });
      setSelectedTicket(res.data);
      setCommentText('');
      await refreshAll();
      notifications.show({
        title: 'Mesaj gönderildi',
        message: 'Yorum talebe eklendi ve kullanıcı bilgilendirildi.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      notifications.show({
        title: 'Mesaj gönderilemedi',
        message: err.response?.data?.detail || 'Lütfen tekrar deneyin.',
        color: 'red',
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedTicket || !statusForm.status) return;
    setSubmittingStatus(true);
    try {
      const res = await api.put(`/it-support/tickets/${selectedTicket.id}/status`, {
        status: statusForm.status,
        resolution_note: statusForm.resolution_note?.trim() || null,
      });
      setSelectedTicket(res.data);
      setStatusForm({
        status: res.data.status || '',
        resolution_note: res.data.resolution_note || '',
      });
      await refreshAll();
      notifications.show({ title: 'Durum güncellendi', message: 'Talep kaydedildi.', color: 'teal' });
    } catch (err) {
      notifications.show({
        title: 'Durum güncellenemedi',
        message: err.response?.data?.detail || 'Lütfen tekrar deneyin.',
        color: 'red',
      });
    } finally {
      setSubmittingStatus(false);
    }
  };

  // Yönetim ekranındaki aktif filtrelerle backend tarafında Excel oluştur.
  const handleExportExcel = async () => {
    if (exportingExcel) return;

    setExportingExcel(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const response = await api.get('/it-support/tickets/export/excel', {
        params,
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      // Content-Disposition bazı CORS yapılandırmalarında okunamayabilir;
      // bu durumda aşağıdaki varsayılan dosya adı kullanılır.
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackName = `SMS_Grup_BT_Destek_Raporu_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const filename = filenameMatch?.[1] || fallbackName;
      const excelBlob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      notifications.show({
        title: 'Excel raporu hazır',
        message: 'BT Destek raporunun indirilmesi başlatıldı.',
        color: 'teal',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      // responseType: blob olduğunda FastAPI hata yanıtı da Blob olarak gelebilir.
      let errorMessage = 'Excel raporu indirilemedi. Lütfen tekrar deneyin.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const payload = JSON.parse(await data.text());
          if (typeof payload.detail === 'string') errorMessage = payload.detail;
        } catch {
          // Boş / JSON olmayan hata yanıtı için genel mesaj kullanılır.
        }
      } else if (typeof data?.detail === 'string') {
        errorMessage = data.detail;
      }

      notifications.show({
        title: 'Excel indirilemedi',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const res = await api.get(`/it-support/attachments/${attachment.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_name || 'dosya';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notifications.show({
        title: 'Dosya indirilemedi',
        message: err.response?.data?.detail || 'Dosyaya erişilemedi.',
        color: 'red',
      });
    }
  };

  const handlePrintTicket = () => {
    if (!selectedTicket) return;
    const popup = window.open('', '_blank', 'width=900,height=1000');
    if (!popup) {
      notifications.show({ title: 'Yazdırma açılamadı', message: 'Popup engelleyiciyi kontrol edin.', color: 'orange' });
      return;
    }

    const escapeHtml = (text) => String(text ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

    const comments = selectedTicket.comments?.length
      ? selectedTicket.comments.map((c) => `
        <div class="comment">
          <b>${escapeHtml(c.user?.username || '-')}</b>
          <span>${escapeHtml(formatDateTime(c.created_at))}</span>
          <p>${escapeHtml(c.comment).replace(/\n/g, '<br/>')}</p>
        </div>`).join('')
      : '<p class="muted">Yorum bulunmuyor.</p>';

    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
      <title>BT Destek #${selectedTicket.id}</title><style>
      body{font-family:Arial,sans-serif;color:#182230;padding:32px;line-height:1.45}.head{display:flex;justify-content:space-between;border-bottom:2px solid #e9ecef;padding-bottom:18px}.muted{color:#6c757d}.box,.comment{border:1px solid #e9ecef;border-radius:10px;padding:14px;margin-top:12px}.label{font-size:12px;color:#6c757d;text-transform:uppercase}.value{font-weight:600;margin-top:4px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.comment span{float:right;color:#6c757d;font-size:12px}.comment p{margin:8px 0 0}@media print{body{padding:0}}
      </style></head><body>
      <div class="head"><div><h2>BT Destek Talebi #${selectedTicket.id}</h2><div class="muted">${escapeHtml(formatDateTime(selectedTicket.created_at))}</div></div><div><b>${escapeHtml(selectedTicket.status)}</b> · ${escapeHtml(selectedTicket.priority)}</div></div>
      <h3>${escapeHtml(selectedTicket.title)}</h3>
      <div class="grid"><div class="box"><div class="label">Personel</div><div class="value">${escapeHtml(employeeName(selectedTicket))}</div></div><div class="box"><div class="label">Kullanıcı</div><div class="value">${escapeHtml(selectedTicket.created_by_user?.username || '-')}</div></div></div>
      <div class="box"><div class="label">Açıklama</div><div class="value">${escapeHtml(selectedTicket.description || '-').replace(/\n/g, '<br/>')}</div></div>
      <div class="box"><div class="label">Çözüm Notu</div><div class="value">${escapeHtml(selectedTicket.resolution_note || '-').replace(/\n/g, '<br/>')}</div></div>
      <h3>Yazışmalar</h3>${comments}<script>window.onload=()=>{window.print();}</script></body></html>`);
    popup.document.close();
  };

  const openCount = useMemo(() => tickets.filter((t) => t.status === 'Açık').length, [tickets]);
  const inProgressCount = useMemo(() => tickets.filter((t) => t.status === 'İşlemde').length, [tickets]);
  const resolvedCount = useMemo(
    () => tickets.filter((t) => ['Çözüldü', 'Kapatıldı'].includes(t.status)).length,
    [tickets]
  );
  const criticalOpenCount = useMemo(
    () => tickets.filter((t) => t.priority === 'Kritik' && ['Açık', 'İşlemde'].includes(t.status)).length,
    [tickets]
  );
  const hasFilters = Boolean(search || statusFilter || priorityFilter);

  return (
    <Stack gap="xl">
      <Paper
        radius="xl"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          background: 'linear-gradient(135deg, #172033 0%, #1f2d45 55%, #234a73 100%)',
          color: 'white',
          boxShadow: '0 14px 35px rgba(23,32,51,.18)',
        }}
      >
        <Group justify="space-between" align="center" gap="lg">
          <Group gap="md" wrap="nowrap">
            <ThemeIcon size={54} radius="xl" color="blue" variant="filled">
              <IconHeadset size={28} />
            </ThemeIcon>
            <div>
              <Group gap="sm">
                <Title order={2} c="white">BT Destek Yönetimi</Title>
                <Badge color="blue" variant="light">Yönetici</Badge>
              </Group>
              <Text c="rgba(255,255,255,.72)" mt={4}>
                Destek kuyruğunu izleyin, önceliklendirin ve talepleri sonuçlandırın.
              </Text>
            </div>
          </Group>
          <Group gap="sm" wrap="wrap">
            <Tooltip label="Arama, durum ve öncelik filtrelerine göre Excel raporu indir">
              <Button
                color="teal"
                radius="md"
                leftSection={<IconFileSpreadsheet size={19} />}
                onClick={handleExportExcel}
                loading={exportingExcel}
              >
                Excel İndir
              </Button>
            </Tooltip>
            <Tooltip label="Tüm verileri yenile">
              <ActionIcon size="lg" radius="md" variant="white" color="dark" onClick={refreshAll}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }}>
        <StatCard
          title="Toplam Kayıt"
          value={overview?.all_total_tickets ?? overview?.total_tickets ?? tickets.length}
          subtitle="Sistemdeki tüm destek talepleri"
          color="blue"
          background="#e7f5ff"
          icon={<IconTicket size={22} />}
        />
        <StatCard
          title="Açık Talepler"
          value={overview?.all_open_tickets ?? openCount}
          subtitle="Müdahale bekleyen talepler"
          color="red"
          background="#fff5f5"
          icon={<IconAlertTriangle size={22} />}
        />
        <StatCard
          title="İşlemde"
          value={overview?.all_in_progress_tickets ?? inProgressCount}
          subtitle="Üzerinde çalışılan kayıtlar"
          color="orange"
          background="#fff4e6"
          icon={<IconClock size={22} />}
        />
        <StatCard
          title="Çözülenler"
          value={overview?.all_resolved_tickets ?? resolvedCount}
          subtitle="Çözülen veya kapatılan talepler"
          color="teal"
          background="#e6fcf5"
          icon={<IconCheck size={22} />}
        />
      </SimpleGrid>

      {criticalOpenCount > 0 && !hasFilters && (
        <Paper withBorder radius="lg" p="md" bg="#fff5f5" style={{ borderColor: '#ffc9c9' }}>
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon color="red" variant="light" radius="xl"><IconAlertTriangle size={19} /></ThemeIcon>
              <div>
                <Text fw={700} size="sm">{criticalOpenCount} kritik talep aksiyon bekliyor</Text>
                <Text size="xs" c="dimmed">Kritik öncelikli açık ve işlemdeki kayıtları kontrol edin.</Text>
              </div>
            </Group>
            <Button size="xs" color="red" variant="light" onClick={() => setPriorityFilter('Kritik')}>
              Kritik Talepleri Göster
            </Button>
          </Group>
        </Paper>
      )}

      <Paper withBorder radius="xl" style={{ overflow: 'hidden', borderColor: '#e9ecef', boxShadow: '0 4px 20px rgba(0,0,0,.03)' }}>
        <Group justify="space-between" p="lg" align="flex-end">
          <div>
            <Title order={4}>Destek Kuyruğu</Title>
            <Text c="dimmed" size="sm" mt={3}>Tüm kullanıcı talepleri ve mevcut durumları</Text>
          </div>
          <Group gap="sm" align="flex-end">
            <TextInput
              label="Ara"
              placeholder="Talep, personel, konu..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setSearch(value);
              }}
              w={250}
            />
            <Select
              label="Durum"
              placeholder="Tümü"
              clearable
              data={STATUS_OPTIONS}
              value={statusFilter || null}
              onChange={(value) => setStatusFilter(value || '')}
              w={145}
            />
            <Select
              label="Öncelik"
              placeholder="Tümü"
              clearable
              data={PRIORITY_OPTIONS}
              value={priorityFilter || null}
              onChange={(value) => setPriorityFilter(value || '')}
              w={145}
            />
            {hasFilters && (
              <Tooltip label="Filtreleri temizle">
                <ActionIcon variant="light" color="gray" size={36} onClick={clearFilters}>
                  <IconFilterOff size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
        <Divider />

        {loading ? (
          <Group justify="center" py={60}><Loader size="sm" /></Group>
        ) : tickets.length === 0 ? (
          <Stack align="center" gap={8} py={60}>
            <ThemeIcon size={50} radius="xl" color="gray" variant="light"><IconTicket size={24} /></ThemeIcon>
            <Text fw={600}>Kayıt bulunamadı</Text>
            <Text size="sm" c="dimmed">Filtreleri değiştirerek tekrar deneyin.</Text>
          </Stack>
        ) : (
          <Table highlightOnHover verticalSpacing="md" horizontalSpacing="lg">
            <Table.Thead>
              <Table.Tr style={{ background: '#f8f9fa' }}>
                <Table.Th>TALEP</Table.Th>
                <Table.Th>PERSONEL</Table.Th>
                <Table.Th>KONU</Table.Th>
                <Table.Th>ÖNCELİK</Table.Th>
                <Table.Th>DURUM</Table.Th>
                <Table.Th>OLUŞTURULMA</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tickets.map((ticket) => (
                <Table.Tr key={ticket.id} onClick={() => openTicketDetail(ticket.id)} style={{ cursor: 'pointer' }}>
                  <Table.Td><Text fw={700} size="sm">#{ticket.id}</Text></Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={32} radius="xl" color="blue">{employeeName(ticket).charAt(0)}</Avatar>
                      <div>
                        <Text size="sm" fw={600}>{employeeName(ticket)}</Text>
                        <Text size="xs" c="dimmed">{ticket.created_by_user?.username || '-'}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={500} lineClamp={1}>{ticket.title}</Text></Table.Td>
                  <Table.Td><Badge variant="light" color={priorityColor(ticket.priority)}>{ticket.priority}</Badge></Table.Td>
                  <Table.Td><Badge variant="dot" color={statusColor(ticket.status)}>{ticket.status}</Badge></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{formatDateTime(ticket.created_at)}</Text></Table.Td>
                  <Table.Td><ActionIcon variant="subtle" color="gray"><IconChevronRight size={18} /></ActionIcon></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title={
          <Group gap="sm">
            <Text fw={700}>Talep #{selectedTicket?.id}</Text>
            {selectedTicket && <Badge variant="dot" color={statusColor(selectedTicket.status)}>{selectedTicket.status}</Badge>}
          </Group>
        }
        position="right"
        size="xl"
        padding="xl"
      >
        {selectedTicket && (
          <Stack gap="lg">
            <Card withBorder radius="lg" p="lg">
              <Group justify="space-between" align="flex-start" gap="md">
                <div style={{ flex: 1 }}>
                  <Text size="xs" fw={700} c="dimmed">TALEP KONUSU</Text>
                  <Text fw={700} size="lg" mt={4}>{selectedTicket.title}</Text>
                  <Text size="xs" c="dimmed" mt={5}>{formatDateTime(selectedTicket.created_at)}</Text>
                </div>
                <Group gap="xs">
                  <Badge variant="light" color={priorityColor(selectedTicket.priority)}>{selectedTicket.priority}</Badge>
                  <Tooltip label="Talebi yazdır">
                    <ActionIcon variant="light" color="gray" onClick={handlePrintTicket}><IconPrinter size={17} /></ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              <Divider my="md" />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Paper bg="#f8f9fa" radius="md" p="sm">
                  <Group gap="sm" wrap="nowrap">
                    <ThemeIcon variant="light" color="blue" radius="xl"><IconUser size={16} /></ThemeIcon>
                    <div><Text size="xs" c="dimmed">Personel</Text><Text size="sm" fw={600}>{employeeName(selectedTicket)}</Text></div>
                  </Group>
                </Paper>
                <Paper bg="#f8f9fa" radius="md" p="sm">
                  <Text size="xs" c="dimmed">Kullanıcı hesabı</Text>
                  <Text size="sm" fw={600}>{selectedTicket.created_by_user?.username || '-'}</Text>
                </Paper>
              </SimpleGrid>
              <Text size="xs" fw={700} c="dimmed" mt="lg" mb={6}>AÇIKLAMA</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description || '-'}</Text>
            </Card>

            {selectedTicket.attachments?.length > 0 && (
              <Card withBorder radius="lg" p="lg">
                <Group justify="space-between" mb="sm">
                  <Title order={5}>Dosya Ekleri</Title>
                  <Badge variant="light" color="gray">{selectedTicket.attachments.length}</Badge>
                </Group>
                <Stack gap="xs">
                  {selectedTicket.attachments.map((file) => (
                    <Paper key={file.id} withBorder radius="md" p="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap"><ThemeIcon color="gray" variant="light"><IconFile size={17} /></ThemeIcon><Text size="sm" fw={500} truncate>{file.original_name || file.filename}</Text></Group>
                        <Button size="xs" variant="light" onClick={() => handleDownloadAttachment(file)}>İndir</Button>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Card>
            )}

            <Card withBorder radius="lg" p="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon variant="light" color="blue"><IconSettings size={18} /></ThemeIcon>
                <div><Title order={5}>Talep Yönetimi</Title><Text size="xs" c="dimmed">Durumu ve çözüm bilgisini güncelleyin.</Text></div>
              </Group>
              <Stack gap="md">
                <Select
                  label="Durum"
                  data={STATUS_OPTIONS}
                  value={statusForm.status}
                  onChange={(value) => setStatusForm((prev) => ({ ...prev, status: value || '' }))}
                />
                <Textarea
                  label="Çözüm Notu"
                  description="Özellikle Çözüldü/Kapatıldı durumunda yapılan işlemi kaydedin."
                  minRows={4}
                  autosize
                  maxRows={8}
                  placeholder="Uygulanan çözümü ve yapılan işlemleri yazın..."
                  value={statusForm.resolution_note}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setStatusForm((prev) => ({ ...prev, resolution_note: value }));
                  }}
                />
                <Group justify="flex-end">
                  <Button leftSection={<IconSettings size={16} />} loading={submittingStatus} onClick={handleUpdateStatus}>Değişiklikleri Kaydet</Button>
                </Group>
              </Stack>
            </Card>

            <Card withBorder radius="lg" p="lg">
              <Group justify="space-between" mb="md">
                <div><Title order={5}>Yazışmalar</Title><Text size="xs" c="dimmed">Kullanıcıyla ticket üzerinden iletişim kurun.</Text></div>
                <Badge variant="light" color="gray">{selectedTicket.comments?.length || 0}</Badge>
              </Group>
              <Stack gap="sm">
                {selectedTicket.comments?.length > 0 ? selectedTicket.comments.map((comment) => (
                  <Paper key={comment.id} bg="#f8f9fa" radius="md" p="sm">
                    <Group justify="space-between" mb={5}>
                      <Text fw={700} size="xs">{comment.user?.username || '-'}</Text>
                      <Text size="xs" c="dimmed">{formatDateTime(comment.created_at)}</Text>
                    </Group>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{comment.comment}</Text>
                  </Paper>
                )) : <Text c="dimmed" size="sm">Henüz bir yazışma bulunmuyor.</Text>}
                <Divider my="xs" />
                <Textarea
                  label="Kullanıcıya mesaj"
                  minRows={3}
                  autosize
                  maxRows={7}
                  placeholder="Yanıtınızı yazın..."
                  value={commentText}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setCommentText(value);
                  }}
                />
                <Group justify="flex-end">
                  <Button variant="light" leftSection={<IconMessageCircle size={16} />} loading={submittingComment} disabled={!commentText.trim()} onClick={handleAddComment}>Mesaj Gönder</Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
